import { TypedEmitter } from "./EventEmitter";
import { AuthTokenStore } from "./AuthTokenStore";
import { HEARTBEAT_INTERVAL_MS } from "./types";
import type { ApiResult, ClientMessage, ServerMessage } from "./types";

export type NetworkEvents = {
  "ws:open": void;
  "ws:close": { code: number; reason: string };
  "ws:error": { message: string };
  "ws:message": ServerMessage;
  "ws:reconnecting": { attempt: number };
  "ws:heartbeat_timeout": void;
}

export interface RestOptions {
  idempotencyKey?: string;
  signal?: AbortSignal;
}

/** Minimal WebSocket-like interface so tests can inject a fake without a
 * real socket/server. The browser's native WebSocket satisfies this. */
export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: string }) => void) | null;
}

export type WebSocketFactory = (url: string) => WebSocketLike;

const defaultWsFactory: WebSocketFactory = (url) => new WebSocket(url) as unknown as WebSocketLike;
const defaultFetch: typeof fetch = (...args) => fetch(...args);

/**
 * Per 2.1 (Team Horizon's module description): "NetworkManager (the only
 * place fetch()/WebSocket are called directly — every scene/panel goes
 * through networkManager.rest.* or its event emitter, never around it)".
 *
 * Two responsibilities:
 *  - `rest.*` — typed wrapper over the gateway's REST API (2.7 envelope)
 *  - WebSocket lifecycle — connect/send/heartbeat/reconnect over the
 *    real-time protocol (2.8), surfaced only via the `on(...)` emitter.
 */
export class NetworkManager {
  readonly rest: {
    get: <T>(path: string, opts?: RestOptions) => Promise<ApiResult<T>>;
    post: <T>(path: string, body?: unknown, opts?: RestOptions) => Promise<ApiResult<T>>;
    put: <T>(path: string, body?: unknown, opts?: RestOptions) => Promise<ApiResult<T>>;
    delete: <T>(path: string, opts?: RestOptions) => Promise<ApiResult<T>>;
  };

  private readonly emitter = new TypedEmitter<NetworkEvents>();
  private readonly apiBase: string;
  private readonly fetchImpl: typeof fetch;
  private readonly wsFactory: WebSocketFactory;
  readonly auth = new AuthTokenStore();

  private ws: WebSocketLike | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private currentWsPath: string | null = null;
  private reconnectAttempt = 0;
  private intentionalClose = false;

  constructor(opts: { apiBase: string; fetchImpl?: typeof fetch; wsFactory?: WebSocketFactory }) {
    this.apiBase = opts.apiBase.replace(/\/+$/, "");
    this.fetchImpl = opts.fetchImpl ?? defaultFetch;
    this.wsFactory = opts.wsFactory ?? defaultWsFactory;

    this.rest = {
      get: (path, restOpts) => this.request("GET", path, undefined, restOpts),
      post: (path, body, restOpts) => this.request("POST", path, body, restOpts),
      put: (path, body, restOpts) => this.request("PUT", path, body, restOpts),
      delete: (path, restOpts) => this.request("DELETE", path, undefined, restOpts),
    };
  }

  // ---- Event surface (2.1: "or its event emitter") ---------------------

  on<K extends keyof NetworkEvents>(event: K, listener: (payload: NetworkEvents[K]) => void): () => void {
    return this.emitter.on(event, listener);
  }

  // ---- REST (2.7) --------------------------------------------------------

  private async request<T>(
    method: "GET" | "POST" | "PUT" | "DELETE",
    path: string,
    body: unknown,
    opts?: RestOptions
  ): Promise<ApiResult<T>> {
    // Per 2.2: every request goes through the ONE gateway base URL. Paths
    // passed in are always relative (e.g. "/api/v1/characters"), never a
    // full URL to a specific Worker.
    if (/^https?:\/\//i.test(path)) {
      throw new Error(
        `NetworkManager.rest received an absolute URL ("${path}") — callers must pass a relative /api/v1/... path so every request routes through the single gateway base URL (2.2).`
      );
    }

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const token = this.auth.get();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (opts?.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

    let response: Response;
    try {
      response = await this.fetchImpl(`${this.apiBase}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: opts?.signal,
      });
    } catch (err) {
      // Network-level failure (offline, DNS, CORS preflight rejected, etc.)
      // — degrades to a typed error result rather than throwing, so a
      // caller's UI can show "couldn't reach server" instead of crashing.
      return {
        ok: false,
        code: "NETWORK_ERROR",
        message: err instanceof Error ? err.message : "Network request failed",
        httpStatus: 0,
      };
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      return {
        ok: false,
        code: "INVALID_RESPONSE",
        message: "Response body was not valid JSON",
        httpStatus: response.status,
      };
    }

    if (!response.ok) {
      const errBody = json as { error?: { code?: string; message?: string; requestId?: string; details?: unknown } };
      return {
        ok: false,
        code: errBody.error?.code ?? "UNKNOWN_ERROR",
        message: errBody.error?.message ?? "Request failed",
        requestId: errBody.error?.requestId,
        details: errBody.error?.details,
        httpStatus: response.status,
      };
    }

    const okBody = json as { data: T; meta: { requestId: string; nextCursor?: string | null } };
    return { ok: true, data: okBody.data, meta: okBody.meta };
  }

  // ---- WebSocket (2.8) ----------------------------------------------------

  /**
   * Connects to a bare WebSocket path already resolved by the caller (e.g.
   * the websocket_url returned from GET /api/v1/zones/:code/connect, per
   * 2.2's documented world-do flow — this method does not itself resolve
   * that URL, it just opens the connection to whatever URL it's given).
   */
  connect(wsUrl: string): void {
    this.intentionalClose = false;
    this.currentWsPath = wsUrl;
    this.openSocket(wsUrl);
  }

  private openSocket(wsUrl: string): void {
    const socket = this.wsFactory(wsUrl);
    this.ws = socket;

    socket.onopen = () => {
      this.reconnectAttempt = 0;
      this.startHeartbeat();
      this.emitter.emit("ws:open", undefined);
    };

    socket.onclose = (ev) => {
      this.stopHeartbeat();
      this.emitter.emit("ws:close", { code: ev.code, reason: ev.reason });
      if (!this.intentionalClose && this.currentWsPath) {
        this.scheduleReconnect();
      }
    };

    socket.onerror = () => {
      this.emitter.emit("ws:error", { message: "WebSocket error" });
    };

    socket.onmessage = (ev) => {
      let parsed: ServerMessage;
      try {
        parsed = JSON.parse(ev.data) as ServerMessage;
      } catch {
        this.emitter.emit("ws:error", { message: "Received malformed WebSocket message" });
        return;
      }

      if (parsed.type === "pong") {
        this.clearPongTimeout();
      }

      this.emitter.emit("ws:message", parsed);
    };
  }

  private scheduleReconnect(): void {
    this.reconnectAttempt += 1;
    const backoffMs = Math.min(1000 * 2 ** this.reconnectAttempt, 15000);
    this.emitter.emit("ws:reconnecting", { attempt: this.reconnectAttempt });
    setTimeout(() => {
      if (this.currentWsPath && !this.intentionalClose) {
        this.openSocket(this.currentWsPath);
      }
    }, backoffMs);
  }

  send<T>(message: ClientMessage<T>): void {
    if (!this.ws || this.ws.readyState !== 1 /* OPEN */) {
      this.emitter.emit("ws:error", { message: "Attempted to send while socket is not open" });
      return;
    }
    this.ws.send(JSON.stringify(message));
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.currentWsPath = null;
    this.stopHeartbeat();
    this.ws?.close(1000, "client disconnect");
    this.ws = null;
  }

  /**
   * Per 2.8: "heartbeat/pong runs on a 4000ms cadence; a connection that
   * misses it is dropped." The server drops connections that miss a
   * heartbeat; the client mirrors this by also giving up on the connection
   * if a pong doesn't arrive within one interval of sending — surfaced as
   * `ws:heartbeat_timeout` so the owning scene can trigger StateManager's
   * `reconnecting` transition.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      // If the previous heartbeat's pong never arrived, don't silently
      // start a fresh cycle over it — that would mask a real missed pong
      // whenever this tick and the pending timeout land in the same
      // macrotask. Surface the timeout now, then proceed with the new
      // heartbeat.
      if (this.pongTimeoutTimer) {
        this.clearPongTimeout();
        this.emitter.emit("ws:heartbeat_timeout", undefined);
      }
      this.send({ type: "heartbeat", payload: {} });
      this.pongTimeoutTimer = setTimeout(() => {
        this.pongTimeoutTimer = null;
        this.emitter.emit("ws:heartbeat_timeout", undefined);
      }, HEARTBEAT_INTERVAL_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    this.clearPongTimeout();
  }

  private clearPongTimeout(): void {
    if (this.pongTimeoutTimer) clearTimeout(this.pongTimeoutTimer);
    this.pongTimeoutTimer = null;
  }
}
